import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog} from '@angular/material/dialog';
import { PageEvent} from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { EmailDomain, EmailDomainPropertyValue, OPERATION, SupplierDocument, TabPermissions } from 'app/profile/models/supplier.model';
import { ContactsService } from 'app/profile/services/contacts.service';
import { GetEmailDomains, GetSupplierDocumentsListByCode, GetSupplierProfileCurrentUser, GetTabPermissions,GetPreventativeInProgress } from 'app/profile/store/app.actions';
import { SelectTabPermissions, SelectEmailDomainList,SelectPreventativeInProgress } from 'app/profile/store/app.selector';
import { SupplierState } from 'app/profile/store/state/supplierstate.state';
import { deepfind, isNullOrEmpty, isNullOrUndefined, transformEmailDomain } from 'app/shared/utils/utils';
import { BehaviorSubject } from 'rxjs';
import { BaseComponent } from '../../base.component';
import { EmailDomainFormComponent } from './email-domain-form/email-domain-form.component';
import { I18nService } from 'app/shared/services/i18n.service';
import { DomainEditComponent } from 'app/common/domain/domain-edit.component';
import { FileUploadService } from 'app/common/services/fileUpload.service';
import { statusClass, statusDisplay } from 'app/shared/constants';

@Component({
  selector: 'email-domain',
  templateUrl: './email-domain.component.html',
  styleUrls: ['./email-domain.component.scss']
})
export class EmailDomainComponent extends BaseComponent implements OnInit, OnDestroy {
  gridViewData: BehaviorSubject<Array<EmailDomainPropertyValue>> = null;
  currentPageSize = 25;
  totalRows = 0;
  pageSizeOptions: number[] = [10, 25, 50, 100, 250];
  displayedColumns = [
    "domain",
    "reason",
    "website",
    "documents",
    "action"
  ];

  supplierCode: string;
  tabPermissions: TabPermissions;
  emailDomainList: EmailDomainPropertyValue[];
  preventativeHold: boolean =false;

  // Status Display Variables
  statusClass = statusClass;
  statusDisplay = statusDisplay;

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private contactService: ContactsService,
    private store: Store<SupplierState>,
    public translate: I18nService,
    private fileUploadService: FileUploadService
  ) {
    super();
    this.gridViewData = new BehaviorSubject([]);
    this.currentPageSize = this.pageSizeOptions[0];
    this.route.parent.params.subscribe(params => {
      if (!params["code"]) {
        return;
      }

      this.supplierCode = params["code"];
      this.store.dispatch(new GetSupplierProfileCurrentUser());
      this.store.dispatch(new GetTabPermissions(this.supplierCode));
      this.store.dispatch(new GetSupplierDocumentsListByCode(this.supplierCode));
      this.store.pipe(select(SelectTabPermissions)).subscribe(tabPermissions => {
        this.tabPermissions = tabPermissions;
      });

      this.store.select(SelectEmailDomainList)
        .subscribe(items => {
          if (items) {
            this.emailDomainList = new Array(...items);
            this.loadGrid(this.currentPageSize, 0);
          }
        });
    });
  }

  ngOnInit() {
    this.initLoad();
    if (this.supplierCode) { 
      this.store.dispatch(new GetPreventativeInProgress(this.supplierCode,true));
      this.store.pipe(select(SelectPreventativeInProgress)).subscribe(x => {
        if (x) {
          this.preventativeHold = x;
        }
      });
    }
  }

  initLoad(isReload: boolean = false) {
    this.store.dispatch(new GetEmailDomains(this.supplierCode, isReload));
  }

  loadGrid(take: number, skip: number) {
    let contactList = this.emailDomainList.slice(skip, skip + take);
    contactList.forEach(x => {
      x.canUpdateItem = this.canChangeItem(x);
      x.canDeleteItem = x.canUpdateItem;
    });
    this.totalRows = this.emailDomainList.length;
    this.gridViewData.next(contactList);
  }

  canChangeItem(dataItem: EmailDomainPropertyValue) {
    if (dataItem && (!(dataItem.stageID && dataItem.stageID.newValue) || (dataItem.isTouched && (dataItem.stageID && dataItem.stageID.newValue)))) {
      var keys = Object.keys(dataItem);
      return keys.some(key => deepfind(dataItem, `${key}.permission`) == "ReadWrite");
    }
    return false;
  }

  pageChange(pageChange: PageEvent) {
    const { previousPageIndex, pageSize, pageIndex } = pageChange;
    this.currentPageSize = pageSize;

    if (previousPageIndex === pageIndex) {
      this.loadGrid(pageSize, 0);
    } else {
      const from = pageIndex * pageSize;
      this.loadGrid(pageSize, from);
    }
  }

  deleteItem(item: EmailDomainPropertyValue) {
    item.operation.newValue = OPERATION.DELETE;
    item.isTouched = true;
    var result = transformEmailDomain(item);
    this.contactService.processEmailDomainChange(this.supplierCode, result, null)
      .subscribe(event => {
        if (event) {
          this.snackBar.open('Success!', null, {
            duration: 5000,
            verticalPosition: 'top',
            panelClass: ['blue-snackbar']
          });
          this.initLoad(true);
        }
      },
        () => {
          this.snackBar.open('Error!', null, {
            duration: 5000,
            verticalPosition: 'top',
            panelClass: ['blue-snackbar']
          });
        });
  }

  openDialog(dataItem: EmailDomainPropertyValue): void {
    const dialogRef = this.dialog.open(DomainEditComponent, {
      minWidth: '1100px',
      data: {
        suppliercode: this.supplierCode,
        data: dataItem
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if(res){
        this.save(res);
      }
    });
  }

  save(emailDomain: EmailDomain) {
    let emailDomainPropertyValue = new EmailDomainPropertyValue();
    emailDomainPropertyValue.domain.newValue = emailDomain.domain;
    emailDomainPropertyValue.operation.newValue = emailDomain.operation;
    if(emailDomain.id){
      emailDomainPropertyValue.id.newValue = emailDomain.id;
    }
    emailDomainPropertyValue.website.newValue = emailDomain.website;
    emailDomainPropertyValue.reason.newValue = emailDomain.reason;
    emailDomainPropertyValue.comment.newValue = emailDomain.comment;
    emailDomainPropertyValue.additionalReason.newValue = emailDomain.additionalReason;

      if (emailDomain?.documentInfos?.length > 0) {
        let file = emailDomain.documentInfos[0].File as File;
        this.fileUploadService.uploadFileToServer([file], this.supplierCode).subscribe(doc => {
          if (doc) {
            const document = new SupplierDocument();
            document.Operation = OPERATION.CREATE;
            document.DocumentID = doc.documentID;
            document.DocumentType = 'CORR-ED';
            document.FileName = doc.fileName;
            document.DocumentVersion = doc.documentVersion;
            document.ReferenceSystem = 'DMS';
            emailDomainPropertyValue.documents.newValue = [document.DocumentID];
            this.processDomainChange(document, emailDomainPropertyValue);
          }
        });
      } else {
        this.processDomainChange(null, emailDomainPropertyValue);
      }
  }

  processDomainChange(document: SupplierDocument, emailDomain: EmailDomainPropertyValue) {
    const result = transformEmailDomain(emailDomain);
    this.contactService.processEmailDomainChange(this.supplierCode, result, document)
      .subscribe(x => {
        this.initLoad(true);
      });
  }
}
